import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Box,
  Switch,
  FormControlLabel,
  CircularProgress,
} from '@mui/material';
import { X } from 'lucide-react';
import { showErrorToast } from '../../../utils/toastUtils';
import * as userApi from '../../../api/Authentication/api';

const UserModal = ({ open, onClose, onSubmit, user, mode = 'add' }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: '',
    status: true,
  });
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        setLoading(true);
        const response = await userApi.getRoles();
        if (response?.data?.results) {
          setRoles(response.data.results);
        } else if (Array.isArray(response?.data)) {
          setRoles(response.data);
        } else {
          setRoles([]);
          console.error('Unexpected roles data format:', response);
        }
      } catch (error) {
        console.error('Error fetching roles:', error);
        showErrorToast(error?.response?.data?.message || 'Failed to fetch roles');
        setRoles([]);
      } finally {
        setLoading(false);
      }
    };

    if (open) {
      fetchRoles();
    }
  }, [open]);

  useEffect(() => {
    if (open && user) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        role: user.roleId || '',
        status: user.status ?? true,
      });
    } else if (open) {
      resetForm();
    }
  }, [open, user]);

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      role: '',
      status: true,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.name) {
      showErrorToast('Please enter a name');
      return;
    }

    if (!formData.email) {
      showErrorToast('Please enter an email');
      return;
    }

    if (!formData.role) {
      showErrorToast('Please select a role');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      showErrorToast('Please enter a valid email address');
      return;
    }

    const submitData = {
      name: formData.name,
      email: formData.email,
      role: mode === 'edit' ? { id: formData.role } : formData.role,
      ...(mode === 'edit' && { is_active: formData.status })
    };

    onSubmit(submitData);
  };

  const title = mode === 'edit' ? 'Edit User' : 'Add User';
  const submitText = mode === 'edit' ? 'Save Changes' : 'Add User';

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 1
        }
      }}
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle
          sx={{
            p: 2,
            fontSize: 18,
            fontWeight: 500,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          {title}
          <IconButton 
            onClick={onClose}
            size="small"
            sx={{ color: 'text.secondary' }}
          >
            <X size={20} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 2 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt:1 }}>
              <TextField
                label="Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                fullWidth
                required
              />

              <TextField
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                fullWidth
                required
                disabled={mode === 'edit'}
              />

              <FormControl fullWidth required>
                <InputLabel>Role</InputLabel>
                <Select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  label="Role"
                >
                  {Array.isArray(roles) && roles.map((role) => (
                    <MenuItem key={role.id} value={role.id}>
                      {role.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {mode === 'edit' && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.checked })}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: '#124F3E',
                          '&:hover': {
                            backgroundColor: 'rgba(18, 79, 62, 0.08)',
                          },
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: '#124F3E',
                        },
                      }}
                    />
                  }
                  label="Active"
                />
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={onClose} sx={{ color: 'text.secondary' }}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            sx={{
              bgcolor: '#124F3E',
              '&:hover': {
                bgcolor: '#0d3e31',
              },
            }}
          >
            {submitText}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default UserModal;
